/* eslint complexity: ["error", 20] */
import {
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  faChevronDown,
  faEdit,
  faHouseChimney,
  faSave,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { isEmpty, union } from 'lodash';
import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useMutation, useQuery } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  CreateSavedViewDocument,
  GetProjectSettingsDocument,
  SavedView,
  ViewSection,
} from '../../../../gql/graphql';
import { useSavedViews } from '../../../../hooks/saved-views';
import { UserType, useUserTenant } from '../../../../services/auth-info';
import { hasDbIds } from '../../../../services/viewer-services';
import { DbIdsPerModel, ModelsMap, useViewer } from '../../ForgeViewer';
import SparkelIcon from '../../icon/SparkelIcon';
import EditSavedViewsModal from './EditSavedViewsModal';
import SaveViewModal from './SaveViewModal';

type ProjectPathParams = {
  projectId: string;
};

type SavedViewsMenuProps = {
  homeFallbackHandler: () => void;
  selectedCategories: Set<string>;
  selectedLayers: Set<string>;
  selectedOrderStatuses: Set<string>;
  activeSection: ViewSection | null;
  setActiveSection: (section: ViewSection | null) => void;
  setCategories: (categories: string[]) => void;
  setSelectedLayers: (layers: string[]) => void;
  setOrderStatuses: (statuses: string[]) => void;
};

const HOME_VIEW_FALLBACK_NAME = 'Home';

const APPLY_GLOBAL_OFFSET = true;

//Decouple from comments in hook?
export default function SavedViewsMenu({
  homeFallbackHandler,
  selectedCategories,
  selectedLayers,
  selectedOrderStatuses,
  setCategories,
  setSelectedLayers,
  setOrderStatuses,
  setActiveSection,
}: SavedViewsMenuProps): ReactElement | null {
  const toast = useToast();
  const user = useUserTenant();
  const canEdit = user.userDetails.userType === UserType.Contractor;

  const {
    viewer,
    loadedModels,
    visibilityManager: {
      hide: hideElements,
      hiddenElements,
      isolatedElements,
      isolate: isolateElements,
      reset: resetVisibility,
      hiddenModelUrns,
      setHiddenModelUrns: setHiddenModels,
    },
  } = useViewer();

  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: openEditModal,
    onClose: closeEditModal,
  } = useDisclosure();

  const { t } = useTranslation('project');

  const { projectId } = useParams<
    keyof ProjectPathParams
  >() as ProjectPathParams;
  const [searchParams, setSearchParams] = useSearchParams();

  const [{ data: getProjectSettings }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });
  const globalOffset =
    getProjectSettings?.project?.settings?.viewerGlobalOffset ?? null;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const {
    savedViews: interactionsSavedViews,
    updateSavedView,
    deleteSavedView,
    saving: isSavingView,
  } = useSavedViews(projectId, false);

  const { savedViews: commentSavedViews } = useSavedViews(projectId, true);

  const activeView = useRef<SavedView | null>(null);

  const [, executeCreateSavedViewMutation] = useMutation(
    CreateSavedViewDocument
  );

  const handleSaveNewView = async (
    name: string,
    includeCameraPosition: boolean,
    setAsHomeView: boolean
  ) => {
    invariant(viewer, 'Viewer is not defined');

    try {
      const result = await executeCreateSavedViewMutation({
        input: {
          savedView: {
            name,
            projectId,
            // TODO: Figure out a proper way to load and restore sectiosn and cutplanes
            // section: activeSection,
            section: null,
            categories: Array.from(selectedCategories),
            layers: Array.from(selectedLayers),
            statuses: Array.from(selectedOrderStatuses),
            hiddenElements: hasDbIds(hiddenElements)
              ? Object.entries(hiddenElements).map(([modelUrn, dbIds]) => ({
                  modelUrn,
                  dbIds,
                }))
              : null,
            isolatedElements: hasDbIds(isolatedElements)
              ? Object.entries(isolatedElements).map(([modelUrn, dbIds]) => ({
                  modelUrn,
                  dbIds,
                }))
              : null,
            hiddenModels: hiddenModelUrns,
            isDefault: setAsHomeView,
            cameraPosition: includeCameraPosition
              ? getCameraPosition(viewer)
              : null,
          },
        },
      });
      toast({
        title: t('toast.success.save-view'),
        status: 'success',
        duration: 2000,
      });
      if (result.data?.createSavedView?.savedView?.id) {
        setSearchParams(
          (prev) => {
            prev.set('view', result.data?.createSavedView?.savedView?.id ?? '');
            return prev;
          },
          { replace: true }
        );
      }
    } catch (err) {
      toast({
        title: t('toast.error.save-view'),
        status: 'error',
        duration: 2000,
      });
    } finally {
      closeModal();
    }
  };

  const handleOverrideViewConfirm = async (
    name: string,
    includeCameraPosition: boolean,
    setAsHomeView: boolean
  ) => {
    const existingView = interactionsSavedViews?.find(
      (view) => view.name === name
    );
    invariant(existingView, 'Existing view not found');
    invariant(globalOffset, 'Global offset is not defined');
    invariant(viewer, 'Viewer is not defined');

    try {
      await updateSavedView(existingView.id, {
        // TODO: Figure out a proper way to load and restore sectiosn and cutplanes
        section: null,
        // section: activeSection,
        isDefault: setAsHomeView,
        categories: Array.from(selectedCategories),
        layers: Array.from(selectedLayers),
        statuses: Array.from(selectedOrderStatuses),
        hiddenElements: hasDbIds(hiddenElements)
          ? Object.entries(hiddenElements).map(([modelUrn, dbIds]) => ({
              modelUrn,
              dbIds,
            }))
          : null,
        isolatedElements: hasDbIds(isolatedElements)
          ? Object.entries(isolatedElements).map(([modelUrn, dbIds]) => ({
              modelUrn,
              dbIds,
            }))
          : null,
        hiddenModels: hiddenModelUrns,
        cameraPosition: includeCameraPosition
          ? getCameraPosition(viewer)
          : null,
      });

      toast({
        title: t('toast.success.save-view'),
        status: 'success',
        duration: 2000,
      });
      setSearchParams(
        (prev) => {
          prev.set('view', existingView.id);
          return prev;
        },
        { replace: true }
      );
    } catch (err) {
      toast({
        title: t('toast.error.save-view'),
        status: 'error',
        duration: 2000,
      });
    } finally {
      closeModal();
    }
  };

  const setAsHomeView = async (id: string) => {
    try {
      await updateSavedView(id, { isDefault: true });
      toast({
        title: t('toast.success.set-home-view'),
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: t('toast.error.set-home-view'),
        status: 'error',
        duration: 2000,
      });
    }
  };

  const renameView = async (id: string, name: string) => {
    try {
      await updateSavedView(id, { name: name });
      toast({
        title: t('toast.success.rename-view'),
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: t('toast.error.rename-view'),
        status: 'error',
        duration: 2000,
      });
    }
  };

  const deleteView = async (id: string) => {
    try {
      await deleteSavedView(id);
      toast({
        title: t('toast.success.delete-view'),
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: t('toast.error.delete-view'),
        status: 'error',
        duration: 2000,
      });
    }
  };

  const loadView = useCallback(
    (savedView: SavedView) => {
      invariant(viewer, 'Viewer is not defined');
      invariant(globalOffset, 'Global offset is not defined');
      activeView.current = savedView;

      resetVisibility();
      if (searchParams.get('view') !== savedView.id) {
        setSearchParams(
          (prev) => {
            prev.set('view', savedView.id);
            return prev;
          },
          {
            replace: !searchParams.has('view'),
          }
        );
      }

      if (savedView.cameraPosition) {
        const {
          positionX,
          positionY,
          positionZ,
          targetX,
          targetY,
          targetZ,
          upX,
          upY,
          upZ,
          aspect,
          fov,
          orthoScale,
          isPerspective,
          applyGlobalOffset,
        } = savedView.cameraPosition;

        viewer.setViewFromArray([
          positionX,
          positionY,
          positionZ,
          applyGlobalOffset ? targetX + globalOffset.x : targetX,
          applyGlobalOffset ? targetY + globalOffset.y : targetY,
          applyGlobalOffset ? targetZ + globalOffset.z : targetZ,
          upX,
          upY,
          upZ,
          aspect,
          fov,
          orthoScale,
          isPerspective ? 1 : 0,
        ]);
      }

      setCategories(savedView.categories);
      setSelectedLayers(savedView.layers);
      setOrderStatuses(savedView.statuses);

      if (savedView.hiddenModels) {
        setHiddenModels(savedView.hiddenModels);
      }

      let elementsToIsolate: DbIdsPerModel = {};
      for (const { modelUrn, dbIds } of savedView.isolatedElements ?? []) {
        elementsToIsolate[modelUrn] = union(
          dbIds,
          // NOTE: We may not need union if we are sure that the arrays are always merged when saving here
          elementsToIsolate[modelUrn] ?? []
        );
      }
      isolateElements(elementsToIsolate);
      let elementsToHide: DbIdsPerModel = {};
      for (const { modelUrn, dbIds } of savedView.hiddenElements ?? []) {
        elementsToHide[modelUrn] = union(
          dbIds,
          // NOTE: We may not need union if we are sure that the arrays are always merged when saving here
          elementsToHide[modelUrn] ?? []
        );
      }
      hideElements(elementsToHide);

      setActiveSection(savedView.section ?? null);
    },
    [
      viewer,
      globalOffset,
      resetVisibility,
      searchParams,
      setCategories,
      setSelectedLayers,
      setOrderStatuses,
      isolateElements,
      hideElements,
      setActiveSection,
      setSearchParams,
      setHiddenModels,
    ]
  );

  useEffect(() => {
    const totalSavedViews: SavedView[] = [];
    if (interactionsSavedViews) {
      totalSavedViews.push(...interactionsSavedViews);
    }
    if (commentSavedViews) {
      totalSavedViews.push(...commentSavedViews);
    }

    if (totalSavedViews.length > 0) {
      const viewId = searchParams.get('view');

      let viewToLoad;
      if (viewId) {
        viewToLoad = totalSavedViews.find(
          (savedView) => savedView.id === viewId
        );
      }
      if (!viewToLoad) {
        viewToLoad = findHomeView(totalSavedViews);
      }

      if (viewToLoad && activeView.current !== viewToLoad) {
        loadView(viewToLoad as SavedView);
      }
    }
  }, [loadView, interactionsSavedViews, commentSavedViews, searchParams]);

  if (interactionsSavedViews === null) {
    return null;
  }

  let homeViewHandler = () => {
    const homeView = findHomeView(interactionsSavedViews);
    setSearchParams(
      (prev) => {
        prev.set('view', homeView?.id ?? HOME_VIEW_FALLBACK_NAME);
        return prev;
      },
      { replace: true }
    );
    if (homeView) {
      loadView(homeView);
    } else {
      homeFallbackHandler();
    }
  };

  const topDownViewHandler = () => {
    if (!viewer) {
      return;
    }
    showTopDownView(viewer, loadedModels);
  };

  const nonHomeViews = getSortedNonHomeViews(interactionsSavedViews);

  const nameValidationRules = {
    required: true,
    // NOTE: These values can obviously be adjusted, but 20 seems reasonable as a start..
    maxLength: {
      value: 100,
      message: 'Name can be at most 100 characters',
    },
  };

  return (
    <>
      <Menu
        closeOnSelect={!isModalOpen}
        isLazy
        // TODO: Consult Magnus on how to handle the 'close event' here
        // TODO: Figure out how scrolling should behave
        // preventOverflow={false}
        // boundary="scrollParent"
      >
        {({ isOpen }) => (
          <>
            <ButtonGroup isAttached size="xs">
              <Tooltip
                // Using this label to remain compliant with our Cypress test suites, which expect a particular label
                label={t('saved-views.home-tooltip')}
                isDisabled={isOpen}
                openDelay={400}
              >
                <Button
                  colorScheme="gray"
                  // Using this label to remain compliant with our Cypress test suites, which expect a particular label
                  aria-label="Reset view"
                  variant="solid-no-transparent"
                  isDisabled={isOpen}
                  onClick={homeViewHandler}
                  leftIcon={<SparkelIcon icon={faHouseChimney} />}
                >
                  {t('saved-views.home')}
                </Button>
              </Tooltip>
              <Tooltip label={t('saved-views.open-menu')} isDisabled={isOpen}>
                <MenuButton
                  as={IconButton}
                  colorScheme="gray"
                  variant="solid-no-transparent"
                  aria-label="Open views menu"
                  icon={<SparkelIcon icon={faChevronDown} />}
                />
              </Tooltip>
            </ButtonGroup>
            <MenuList
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              // TODO: Figure out how scrolling should behave
              // onScroll={(e) => {
              //   e.stopPropagation();
              // }}
            >
              <MenuItem
                onClick={topDownViewHandler}
                display="flex"
                // A trick to make the icon appear on the right
                flexDir={'row-reverse'}
                icon={
                  <SparkelIcon
                    icon={faVideo}
                    color={['gray.300', 'gray.500']}
                    fixedWidth
                  />
                }
              >
                <Text noOfLines={1} maxWidth={'90%'} wordBreak="break-all">
                  {t('saved-views.top-down-view')}
                </Text>
              </MenuItem>
              <Divider />
              {nonHomeViews.length > 0
                ? nonHomeViews.map((savedView) => (
                    <MenuItem
                      key={savedView.id}
                      onClick={() => void loadView(savedView as SavedView)}
                      display="flex"
                      // A trick to make the icon appear on the right
                      flexDir={savedView.cameraPosition ? 'row-reverse' : 'row'}
                      icon={
                        savedView.cameraPosition ? (
                          <SparkelIcon
                            icon={faVideo}
                            color={['gray.300', 'gray.500']}
                            fixedWidth
                          />
                        ) : undefined
                      }
                    >
                      <Text
                        noOfLines={1}
                        maxWidth={'90%'}
                        wordBreak="break-all"
                      >
                        {savedView.name}
                      </Text>
                    </MenuItem>
                  ))
                : null}
              {nonHomeViews.length > 0 && canEdit ? <Divider /> : null}
              {canEdit ? (
                <>
                  <MenuItem
                    icon={<SparkelIcon icon={faSave} fixedWidth />}
                    closeOnSelect={false}
                    onClick={openModal}
                  >
                    {t('saved-views.save-view')}
                  </MenuItem>
                  {interactionsSavedViews.length > 0 ? (
                    <MenuItem
                      icon={<SparkelIcon icon={faEdit} fixedWidth />}
                      closeOnSelect={false}
                      onClick={openEditModal}
                    >
                      {t('saved-views.edit-view')}
                    </MenuItem>
                  ) : null}
                </>
              ) : null}
            </MenuList>
          </>
        )}
      </Menu>
      <EditSavedViewsModal
        isOpen={isEditModalOpen}
        views={interactionsSavedViews}
        onClose={closeEditModal}
        onRename={renameView}
        onDelete={deleteView}
        onSetAsHomeView={setAsHomeView}
        nameRegisterOptions={nameValidationRules}
      />
      <SaveViewModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSaveNewView={handleSaveNewView}
        onOverrideViewConfirm={handleOverrideViewConfirm}
        isSavingView={isSavingView}
        nameRegisterOptions={nameValidationRules}
        savedViews={interactionsSavedViews}
      />
    </>
  );
}

function findHomeView(savedViews: SavedView[]) {
  let homeView = savedViews.find((savedView) => savedView.isDefault);
  if (!homeView) {
    homeView = savedViews.find(
      (savedView) => savedView.name === HOME_VIEW_FALLBACK_NAME
    );
  }
  return homeView;
}

function getSortedNonHomeViews(savedViews: SavedView[]) {
  let nonHomeViews = savedViews.filter(
    (savedView) =>
      !savedView.isDefault &&
      // Check if name is a guid and filter out if it is
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        savedView.name
      )
  );
  if (nonHomeViews.length === savedViews.length) {
    nonHomeViews = savedViews.filter(
      (savedView) => savedView.name !== HOME_VIEW_FALLBACK_NAME
    );
  }
  return nonHomeViews.sort((a, b) => a.name.localeCompare(b.name));
}

export function getCameraPosition(viewer: Autodesk.Viewing.GuiViewer3D) {
  const cameraPosition = viewer.getViewArrayFromCamera();
  // Using target from navigation as the target obtained from the view array
  // is set to selected dbid or model center - not the center of the viewer
  const target = viewer.navigation.getTarget();
  return {
    positionX: cameraPosition[0],
    positionY: cameraPosition[1],
    positionZ: cameraPosition[2],
    targetX: target.x,
    targetY: target.y,
    targetZ: target.z,
    upX: cameraPosition[6],
    upY: cameraPosition[7],
    upZ: cameraPosition[8],
    aspect: cameraPosition[9],
    fov: cameraPosition[10],
    orthoScale: cameraPosition[11],
    isPerspective: cameraPosition[12] === 1,
    applyGlobalOffset: APPLY_GLOBAL_OFFSET,
  };
}

// Show the model(s) looking straight down, Y-axis is oriented north-south and the x-axis is east-west
function showTopDownView(
  viewer: Autodesk.Viewing.GuiViewer3D,
  models: ModelsMap
) {
  if (isEmpty(models)) {
    return;
  }

  const bounds = Object.values(models)
    .map((model) => model.getFuzzyBox({}))
    .reduce((union, box) => union.union(box));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const fit = viewer.navigation.computeFit(
    bounds.center().add(new THREE.Vector3(0, 0, bounds.max.z)),
    bounds.center(),
    viewer.navigation.getVerticalFov(),
    bounds
  );
  viewer.navigation.setView(fit.position, fit.target);
  viewer.navigation.orientCameraUp(true);
  viewer.navigation.setPivotPoint(bounds.center());
}
